import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Formik, Form, useFormikContext } from "formik";
import { ITempProfileData } from "../../../../../redux/profile/tempProfiles/slice";
import { IProfile } from "../../hooks/useProfileData";
import { validateChildBirthDate } from "../../../../../helpers/dateFormatter";
import profileIcon from "../../../../../assets/icons/profile-focus.svg";
import styles from "./ProfileCard.module.scss";
import { DateInput, NameInput, PhoneInput } from "../../../../../components";

const FormikReduxSync: React.FC<{
  profileId: string;
  profileData: ITempProfileData;
  updateProfile: (profileId: string, field: keyof ITempProfileData, value: string) => void;
}> = ({ profileId, profileData, updateProfile }) => {
  const { values } = useFormikContext<{ name: string; phone: string; birthDate: string }>();

  useEffect(() => {
    if (values.name !== profileData.name) {
      updateProfile(profileId, "name", values.name);
    }
    if (values.phone !== profileData.phone) {
      updateProfile(profileId, "phone", values.phone);
    }
    if (values.birthDate !== profileData.birthDate) {
      updateProfile(profileId, "birthDate", values.birthDate);
    }
  }, [values.name, values.phone, values.birthDate, profileId, profileData, updateProfile]);

  return null;
};

interface ProfileCardProps {
  profile: IProfile;
  index: number;
  subscriptionTitle: string;
  price?: number;
  tempProfilesData: { [profileId: string]: ITempProfileData };
  updateProfile: (profileId: string, field: keyof ITempProfileData, value: string) => void;
  validateProfile: (profileData: ITempProfileData, profileType: string) => boolean;
  showPrice?: boolean;
  profileNumber?: number;
}

export const ProfileCard: React.FC<ProfileCardProps> = ({
  profile,
  index,
  subscriptionTitle,
  price,
  tempProfilesData,
  updateProfile,
  validateProfile,
  showPrice = true,
  profileNumber,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdult = profile.type === "adult" || profile.type === "daily";
  const isChild = profile.type === "child";
  const profileNum = profileNumber || index + 1;
  const profileData = tempProfilesData[profile.id] || {
    name: "",
    phone: "",
    email: "",
    birthDate: "",
    message: "",
  };

  const isValid = validateProfile(profileData, profile.type);

  const getBirthDateError = () => {
    if (!isChild) return undefined;
    if (!profileData.birthDate) return undefined;
    const birthDateValidation = validateChildBirthDate(profileData.birthDate);
    return birthDateValidation.isValid ? undefined : birthDateValidation.error;
  };

  const handleSelectProfile = () => {
    const currentPath = location.pathname;
    navigate("/subscriptions-profiles", {
      state: {
        profileId: profile.id,
        profileType: profile.type,
        profileNumber: profileNum,
        returnPath: currentPath,
      },
    });
  };

  const formatPrice = (value?: number) => {
    if (value == null) return undefined;
    if (typeof value === "number") return value.toLocaleString("ru-RU");
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed.toLocaleString("ru-RU") : value;
  };

  const initialValues = {
    name: profileData.name || "",
    phone: profileData.phone || "",
    birthDate: profileData.birthDate || "",
  };

  return (
    <div className={styles.profileCard}>
      <div className={styles.header}>
        <h3 className={styles.title}>
          {subscriptionTitle}
        </h3>
        {showPrice && price !== undefined && (
          <span className={styles.price}>
            {formatPrice(price)} ₽
          </span>
        )}
      </div>

      <div className={styles.profileNumberContainer}>
        <p className={styles.profileNumber}>
          {isAdult ? `Взрослый №${profileNum}` : `Детский №${profileNum}`}
        </p>
        <button
          className={styles.selectButton}
          onClick={handleSelectProfile}
        >
          <img src={profileIcon} alt="Профиль" className={styles.selectButtonIcon} />
          <span className={styles.selectButtonText}>
            Выбрать
          </span>
        </button>
      </div>

      

      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={() => {}}
      >
        {({ setFieldValue }) => (
          <Form>
            <FormikReduxSync
              profileId={profile.id}
              profileData={profileData}
              updateProfile={updateProfile}
            />
            <div className={styles.fields}>
              <NameInput
                lable="ФИО"
                name="name"
                focusThisInput={false}
                placeholder="Введите ФИО"
              />

              {isAdult && (
                <PhoneInput
                  lable="Телефон"
                  name="phone"
                  focusThisInput={false}
                  placeholder="+7 (999) 456-78-09"
                />
              )}

              {isChild && (
                <DateInput
                  label="Дата рождения"
                  name="birthDate"
                  focusThisInput={false}
                  placeholder="DD.MM.YYYY"
                  onChangeFunc={(values) => {
                    if (values.date) {
                      setFieldValue("birthDate", values.date);
                      updateProfile(profile.id, "birthDate", values.date);
                    }
                  }}
                />
              )}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
